import { Suspense } from "react";
import { lazyView } from "@/platform/client";
import { IconChevronLeft, IconChevronRight } from "@/platform/ui";
import { cx } from "@/platform/ui";
import { RIGHT_PANELS, type RightPanelKey } from "@/shell/layout/rightPanels.js";
import { RightSidebarOverview } from "@/shell/layout/RightSidebarOverview.js";

/** The chat and the Markdown it renders, a chunk of their own. */
export const RightSidebarAthena = lazyView(() =>
  import("@/shell/layout/RightSidebarAthena.js").then((m) => m.RightSidebarAthena),
);

// Shared between the collapsed rail (icon-only) and the expanded header strip
// (icon+label) — editing RIGHT_PANELS is what keeps both in sync.
function RightPanelSwitcher({
  active,
  onSelect,
  variant,
}: {
  active: RightPanelKey;
  onSelect: (key: RightPanelKey) => void;
  variant: "rail" | "header";
}) {
  if (variant === "rail") {
    return (
      <div className="flex flex-col gap-1.5">
        {RIGHT_PANELS.map((p) => {
          const Icon = p.icon;
          const isActive = active === p.key;
          return (
            <button
              key={p.key}
              onClick={() => onSelect(p.key)}
              title={p.label}
              className={cx(
                "flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border transition-colors",
                isActive
                  ? "border-accent bg-accent-soft text-accent"
                  : "border-line-strong bg-surface-2 text-muted hover:text-ink",
              )}
            >
              <Icon size={16} />
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      {RIGHT_PANELS.map((p) => {
        const Icon = p.icon;
        const isActive = active === p.key;
        return (
          <button
            key={p.key}
            onClick={() => onSelect(p.key)}
            className={cx(
              "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold transition-colors",
              isActive
                ? "bg-accent-soft text-accent"
                : "text-muted hover:bg-surface-2 hover:text-ink",
            )}
          >
            <Icon size={14} />
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export function RightSidebar({
  collapsed,
  onToggle,
  panel,
  onPanelChange,
}: {
  collapsed: boolean;
  onToggle: () => void;
  panel: RightPanelKey;
  onPanelChange: (key: RightPanelKey) => void;
}) {
  // Clicking a rail icon while collapsed both switches the panel and expands
  // into it — "sends you to that bar." The plain chevron toggle never touches
  // `panel`, so expanding via the chevron always lands wherever it was last.
  const selectFromRail = (key: RightPanelKey) => {
    onPanelChange(key);
    if (collapsed) onToggle();
  };

  if (collapsed) {
    return (
      <aside className="sticky top-0 hidden h-screen w-12 flex-none flex-col items-center gap-2.5 border-l border-line bg-[#0c0d10] pt-3.5 lg:flex">
        <button
          onClick={onToggle}
          title="Open panel"
          className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-line-strong bg-surface-2 text-accent hover:border-accent"
        >
          <IconChevronLeft size={16} />
        </button>
        <RightPanelSwitcher active={panel} onSelect={selectFromRail} variant="rail" />
      </aside>
    );
  }

  return (
    <aside className="sticky top-0 hidden h-screen w-[344px] flex-none flex-col border-l border-line bg-[#0c0d10] lg:flex">
      <div className="flex h-10 flex-none items-center justify-between border-b border-line px-2.5">
        <RightPanelSwitcher active={panel} onSelect={onPanelChange} variant="header" />
        <button
          onClick={onToggle}
          title="Hide panel"
          className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg border border-line bg-surface-2 text-muted hover:text-ink"
        >
          <IconChevronRight size={15} />
        </button>
      </div>

      {panel === "overview" ? (
        <RightSidebarOverview />
      ) : (
        <Suspense fallback={null}>
          <RightSidebarAthena />
        </Suspense>
      )}
    </aside>
  );
}
